import { router } from '@inertiajs/react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { useState, useEffect } from 'react'
import type { ReimbursementItem, Category } from '@/types'
import { CheckIcon } from 'lucide-react'

interface EditItemDialogProps {
  item: ReimbursementItem | null
  categories: Category[]
  onClose: () => void
}

export function EditItemDialog({ item, categories, onClose }: EditItemDialogProps) {
  const [form, setForm] = useState({
    date: '',
    memo: '',
    amount: 0,
    category_id: null as number | null,
    has_tax: false,
  })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (item) {
      setForm({
        date: item.date || '',
        memo: item.memo || '',
        amount: item.amount ? Math.abs(item.amount.cents) : 0,
        category_id: item.category_id,
        has_tax: item.has_tax,
      })
    }
  }, [item])

  if (!item) return null

  function handleSave() {
    setSaving(true)
    router.patch(`/reimbursement_items/${item!.id}`, {
      reimbursement_item: {
        date: form.date,
        memo: form.memo,
        amount: form.amount,
        category_id: form.category_id,
        has_tax: form.has_tax,
      },
    }, {
      preserveScroll: true,
      onSuccess: () => { setSaving(false); onClose() },
      onError: () => setSaving(false),
    })
  }

  function handleSaveAndApprove() {
    setSaving(true)
    router.patch(`/reimbursement_items/${item!.id}`, {
      reimbursement_item: {
        date: form.date,
        memo: form.memo,
        amount: form.amount,
        category_id: form.category_id,
        has_tax: form.has_tax,
      },
    }, {
      preserveScroll: true,
      onSuccess: () => {
        router.post(`/reimbursement_items/${item!.id}/approve`, {}, {
          preserveScroll: true,
          onSuccess: () => { setSaving(false); onClose() },
          onError: () => setSaving(false),
        })
      },
      onError: () => setSaving(false),
    })
  }

  const receiptUrl = item.receipt?.url
  const isImage = item.receipt?.content_type?.startsWith('image/')

  return (
    <Dialog open={!!item} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[15px]">Review Receipt</DialogTitle>
        </DialogHeader>

        {receiptUrl && (
          <div className="rounded-lg border overflow-hidden bg-muted">
            {isImage ? (
              <img
                src={receiptUrl}
                alt="Receipt"
                className="w-full max-h-[50vh] object-contain"
              />
            ) : (
              <iframe
                src={receiptUrl}
                title="Receipt PDF"
                className="w-full h-[50vh]"
              />
            )}
          </div>
        )}

        <div className="space-y-3">
          <div>
            <Label className="text-[13px]">Date</Label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-[13px]">Memo</Label>
            <Input
              value={form.memo}
              onChange={(e) => setForm({ ...form, memo: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-[13px]">Amount</Label>
            <Input
              type="number"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-[13px]">Category</Label>
            <Select
              value={form.category_id?.toString() || '__none__'}
              onValueChange={(value) =>
                setForm({ ...form, category_id: value === '__none__' ? null : Number(value) })
              }
            >
              <SelectTrigger className="mt-1 text-[13px]">
                <SelectValue placeholder="Uncategorized" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Uncategorized</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="has_tax"
              checked={form.has_tax}
              onCheckedChange={(checked) => setForm({ ...form, has_tax: !!checked })}
            />
            <Label htmlFor="has_tax" className="text-[13px]">Includes tax</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="outline" size="sm" onClick={handleSave} disabled={saving}>
            Save
          </Button>
          <Button size="sm" onClick={handleSaveAndApprove} disabled={saving}>
            <CheckIcon className="h-3.5 w-3.5 mr-1.5" />
            Save & Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
